import { useCallback, useEffect, useState } from 'react';

/** Design breakpoints (index.html "반응형 기준"). */
export const BP = { wide: 1440, laptop: 1200, tablet: 900, phone: 600, cover: 360 } as const;

/** Window width, debounced like the design's relayout (150 ms). */
export function useViewportWidth(): number {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    let timer = 0;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setWidth(window.innerWidth), 150);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  return width;
}

export type Posture = '' | 'span-h' | 'span-v';

function readPosture(allowParam: boolean): Posture {
  if (typeof window === 'undefined') return '';
  if (allowParam) {
    const param = new URLSearchParams(window.location.search).get('posture');
    if (param === 'span-h' || param === 'span-v') return param;
  }
  if (window.matchMedia?.('(horizontal-viewport-segments: 2)').matches) return 'span-h';
  if (window.matchMedia?.('(vertical-viewport-segments: 2)').matches) return 'span-v';
  return '';
}

/**
 * Foldable posture from the Viewport Segments API (book = span-h, tabletop =
 * span-v). `?posture=` simulation is accepted only when `allowParam` (dev/fixture).
 * Applies `html.span-h` / `html.span-v` like the design.
 */
export function usePosture(allowParam: boolean): Posture {
  const [posture, setPosture] = useState<Posture>(() => readPosture(allowParam));
  useEffect(() => {
    const update = () => setPosture(readPosture(allowParam));
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [allowParam]);
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('span-h', posture === 'span-h');
    root.classList.toggle('span-v', posture === 'span-v');
    root.dataset.posture = posture;
  }, [posture]);
  return posture;
}

export type MobileView = 'list' | 'chat' | 'detail';

/** How deep each phone view is: going to a shallower one is going back (it slides in from the other side). */
const DEPTH: Record<MobileView, number> = { list: 0, chat: 1, detail: 2 };

/** Panel state of the messenger shell (design toggleDetail / setView / relayout). */
export function useShellLayout(width: number, posture: Posture, initialView: MobileView) {
  const [nav, setNav] = useState<{ view: MobileView; back: boolean }>({ view: initialView, back: false });
  const setView = useCallback(
    (next: MobileView | ((view: MobileView) => MobileView)) =>
      setNav((prev) => {
        const view = typeof next === 'function' ? next(prev.view) : next;
        return view === prev.view ? prev : { view, back: DEPTH[view] < DEPTH[prev.view] };
      }),
    [],
  );
  const view = nav.view;
  /** ≥1440: the docked info panel is shown unless the user closed it. */
  const [wideDetail, setWideDetail] = useState(true);
  /** <1440: the floating info sheet. */
  const [overlayDetail, setOverlayDetail] = useState(false);
  const wide = width >= BP.wide && !posture;
  const single = width < BP.phone && posture !== 'span-h';

  useEffect(() => {
    if (wide) setOverlayDetail(false);
  }, [wide]);

  const toggleDetail = useCallback(
    (force?: boolean) => {
      if (single) {
        setView((v) => (force === false ? 'chat' : force === true ? 'detail' : v === 'detail' ? 'chat' : 'detail'));
        return;
      }
      if (wide) setWideDetail((open) => force ?? !open);
      else setOverlayDetail((open) => force ?? !open);
    },
    [single, wide],
  );

  const detailOpen = wide ? wideDetail : overlayDetail;
  const shellClass = ['shell', wide && !wideDetail && 'no-detail', !wide && overlayDetail && 'detail-open'].filter(Boolean).join(' ');
  return { view, back: nav.back, setView, toggleDetail, detailOpen, shellClass, wide, single };
}
